import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import image from "../../../assets/logo/authLogo.png";
import appConfig from "../../config.json";
import { centrePdfText } from "./pdf";

const MARGIN = 14;
const BORDER = [222, 226, 230];
const SHADE = [241, 241, 241];
const TEXT = [33, 37, 41];
const ACCENT = [250, 173, 20];

const LAYOUTS = [
  {
    logoW: 80,
    logoH: 25,
    font: 10,
    tableFont: 9,
    padding: 2.5,
    line: 5,
    gap: 6,
    signatureH: 18,
  },
  {
    logoW: 64,
    logoH: 20,
    font: 9,
    tableFont: 8.5,
    padding: 1.8,
    line: 4.5,
    gap: 4,
    signatureH: 14,
  },
  {
    logoW: 52,
    logoH: 16,
    font: 8,
    tableFont: 7.5,
    padding: 1.2,
    line: 4,
    gap: 3,
    signatureH: 11,
  },
  {
    logoW: 44,
    logoH: 14,
    font: 7,
    tableFont: 6.5,
    padding: 0.8,
    line: 3.5,
    gap: 2,
    signatureH: 9,
  },
];

const tableStyles = (layout, fontSize = layout.font) => ({
  theme: "grid",
  margin: { left: MARGIN, right: MARGIN, top: 10, bottom: 10 },
  styles: {
    fontSize,
    textColor: TEXT,
    lineColor: BORDER,
    lineWidth: 0.2,
    cellPadding: layout.padding,
    overflow: "linebreak",
  },
});

export const renderSinglePageForm = (draw) => {
  let doc;
  for (const [index, layout] of LAYOUTS.entries()) {
    doc = new jsPDF();
    const finalY = draw(doc, layout);
    const borderBottom = finalY + layout.gap + 6;
    const fits =
      doc.getNumberOfPages() === 1 &&
      borderBottom <= doc.internal.pageSize.height - MARGIN;
    if (fits || index === LAYOUTS.length - 1) {
      drawFormBorder(doc, finalY + layout.gap);
      break;
    }
  }
  return doc;
};

export const formatPdfDate = (date) =>
  date && moment(date).isValid()
    ? moment(date).format("MMMM Do YYYY, h:mm:ss a")
    : "";

export const personName = (person) =>
  [person?.first_name, person?.other_names].filter(Boolean).join(" ");

export const drawFormHeader = (doc, title, layout = LAYOUTS[0]) => {
  const pageWidth = doc.internal.pageSize.width;
  const { logoW, logoH, font, line, gap } = layout;
  doc.addImage(image, "PNG", (pageWidth - logoW) / 2, 12, logoW, logoH);
  doc.setTextColor(...TEXT);
  doc.setFontSize(font);
  let y = 12 + logoH + gap + 1;
  centrePdfText(
    doc,
    "P.O.BOX 610029, Maracha, Uganda | Adongoro Cell, Maracha Town Council",
    y
  );
  centrePdfText(doc, "Mob: +256 393102379", (y += line));
  centrePdfText(
    doc,
    "Web: www.amaniinitiative.org | Email: info.amaniinitiative@gmail.com",
    (y += line)
  );
  doc.setFontSize(font + 1);
  doc.setFont(undefined, "bold");
  centrePdfText(doc, title, (y += line + gap / 2 + 2));
  doc.setFont(undefined, "normal");
  doc.setFontSize(font);
  return y + gap / 2 + 1;
};

export const drawDetailsTable = (doc, rows, startY, layout = LAYOUTS[0]) => {
  doc.autoTable({
    ...tableStyles(layout),
    startY,
    body: rows.map(([label, value]) => [label, value ?? ""]),
    columnStyles: { 0: { cellWidth: 60 } },
    didParseCell: ({ row, cell }) => {
      if (row.index % 2 === 0) cell.styles.fillColor = SHADE;
    },
  });
  return doc.lastAutoTable.finalY;
};

export const drawExpensesTable = (
  doc,
  title,
  columns,
  rows,
  totalRow,
  startY,
  layout = LAYOUTS[0]
) => {
  doc.setFont(undefined, "bold");
  doc.text(title, MARGIN, startY + layout.gap + 2);
  doc.setFont(undefined, "normal");
  doc.autoTable({
    ...tableStyles(layout, layout.tableFont),
    startY: startY + layout.gap + 4,
    head: [columns],
    body: [...rows, totalRow],
    headStyles: {
      fillColor: [250, 250, 250],
      textColor: TEXT,
      fontStyle: "bold",
    },
    didParseCell: ({ section, row, cell, table }) => {
      if (section === "body" && row.index === table.body.length - 1) {
        cell.styles.textColor = ACCENT;
        cell.styles.fontStyle = "bold";
      }
    },
  });
  return doc.lastAutoTable.finalY;
};

export const drawApprovalTable = (
  doc,
  signatories,
  startY,
  layout = LAYOUTS[0]
) => {
  const body = [];
  const signatures = {};
  signatories.forEach(({ label, person, date }) => {
    body.push([
      `${label} By: ${personName(person)}`,
      `Date ${label}: ${formatPdfDate(date)}`,
    ]);
    signatures[body.length] = person?.signature;
    body.push([`Role: ${person?.role || ""}`, "Signature:"]);
  });

  doc.autoTable({
    ...tableStyles(layout),
    startY: startY + layout.gap,
    body,
    didParseCell: ({ row, cell }) => {
      if (row.index % 2 === 0) cell.styles.fillColor = SHADE;
      else cell.styles.minCellHeight = layout.signatureH;
    },
    didDrawCell: ({ section, row, column, cell }) => {
      const signature = signatures[row.index];
      if (section !== "body" || column.index !== 1 || !signature) return;
      try {
        doc.addImage(
          `${appConfig.server_url}${signature}`,
          "PNG",
          cell.x + 24,
          cell.y + 1,
          32,
          cell.height - 2
        );
      } catch (error) {}
    },
  });
  return doc.lastAutoTable.finalY;
};

export const drawFormBorder = (doc, finalY) => {
  if (doc.getNumberOfPages() > 1) return;
  const pageWidth = doc.internal.pageSize.width;
  doc.setDrawColor(...BORDER);
  doc.roundedRect(
    MARGIN - 4,
    8,
    pageWidth - 2 * (MARGIN - 4),
    finalY - 2,
    2,
    2
  );
};
